import React, { useState } from "react";
import { Share2, X } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const SHARE_TARGETS = [
  {
    id: "x",
    label: "X (Twitter)",
    color: "bg-slate-900 hover:bg-slate-800 text-white",
    getUrl: (text, url) =>
      `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
  },
  {
    id: "facebook",
    label: "Facebook",
    color: "bg-blue-600 hover:bg-blue-700 text-white",
    getUrl: (text, url) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
  },
  {
    id: "copy",
    label: "Copy Link",
    color: "bg-slate-100 hover:bg-slate-200 text-slate-700",
    action: "copy",
  },
];

export const ShareButton = ({ text, url, className = "" }) => {
  const [open, setOpen] = useState(false);

  const handleShare = async (target) => {
    if (target.action === "copy") {
      try {
        await navigator.clipboard.writeText(url || window.location.href);
        toast.success("Link copied!");
      } catch (err) {
        toast.error("Failed to copy");
      }
      setOpen(false);
      return;
    }

    // Try Web Share API on mobile first
    if (navigator.share && /mobile|android|iphone/i.test(navigator.userAgent)) {
      try {
        await navigator.share({ title: text, url: url || window.location.href });
        setOpen(false);
        return;
      } catch (err) {
        // Fallback to URL-based sharing
      }
    }

    const shareUrl = target.getUrl(text, url || window.location.href);
    window.open(shareUrl, "_blank", "width=600,height=400");
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(!open)}
        className="text-sky-500 hover:text-sky-700"
        data-testid="share-btn"
      >
        <Share2 className="w-4 h-4" />
      </Button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-xl border border-sky-100 p-2 min-w-[160px] animate-in fade-in zoom-in-95 duration-200"
            data-testid="share-dropdown"
          >
            {SHARE_TARGETS.map((target) => (
              <button
                key={target.id}
                onClick={() => handleShare(target)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${target.color} mb-1 last:mb-0`}
                data-testid={`share-${target.id}`}
              >
                {target.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
